import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchByModule, type ModuleReportRow } from '../../api/admin.js';
import { nanoToUsd, nanoToUsdCompact, formatNumber } from '../../lib/format.js';
import type { CsvColumn } from '../../lib/csv.js';
import { ReportFilters, useReportFilters } from './components/ReportFilters.js';
import { ExportCsvButton } from './components/ExportCsvButton.js';
import { MetricChart, type MetricSeries } from './components/MetricChart.js';

const NO_MODULE = '(sin módulo)';

const CSV_COLUMNS: CsvColumn<ModuleReportRow>[] = [
  { header: 'module', value: (r) => r.module ?? NO_MODULE },
  { header: 'cost_usd', value: (r) => nanoToUsd(r.costNano) },
  { header: 'cost_nano', value: (r) => r.costNano },
  { header: 'sessions', value: (r) => r.sessionCount },
  { header: 'active_users', value: (r) => r.activeUsers },
  { header: 'cost_per_session_usd', value: (r) => nanoToUsd(r.costPerSessionNano) },
  { header: 'calls', value: (r) => r.callCount },
];

export function ModuleReportPage(): ReactElement {
  const filters = useReportFilters();

  const query = useQuery({
    queryKey: ['admin', 'reports', 'by-module', filters.key],
    queryFn: () => fetchByModule(filters.toParams()),
  });

  const rows = query.data ?? [];

  // Chart: barras apiladas módulo × kind (ya viene ordenado por costo desde el backend).
  const chart = useMemo(() => {
    const categories = rows.map((r) => r.module ?? NO_MODULE);
    const kindSet = new Set<string>();
    for (const r of rows) for (const k of r.kindBreakdown) kindSet.add(k.kind);
    const series: MetricSeries[] = [...kindSet].sort().map((kind) => ({
      key: kind,
      values: rows.map((r) => {
        const hit = r.kindBreakdown.find((k) => k.kind === kind);
        return hit ? Number(BigInt(hit.costNano)) : 0;
      }),
    }));
    return { categories, series };
  }, [rows]);

  const grandTotalNano = useMemo(
    () => rows.reduce((a, r) => a + BigInt(r.costNano), 0n).toString(),
    [rows],
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Costo por módulo</h1>
          <p className="page-sub">
            Gasto real por módulo vertical ·{' '}
            <span className="mono">
              {filters.from} → {filters.to}
            </span>{' '}
            · <span className="mono">{nanoToUsdCompact(grandTotalNano)}</span> total
          </p>
        </div>
        <ExportCsvButton filename="by-module" rows={rows} columns={CSV_COLUMNS} />
      </div>

      <ReportFilters state={filters} />

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <div className="pad-7">
          <section className="card" style={{ marginBottom: 24 }}>
            <header className="card-head">
              <h3 className="card-title">Gasto apilado por módulo (× kind)</h3>
            </header>
            <MetricChart
              categories={chart.categories}
              series={chart.series}
              formatValue={(n) => nanoToUsdCompact(BigInt(Math.round(n)).toString())}
            />
          </section>

          <section className="card">
            <header className="card-head">
              <h3 className="card-title">Breakdown</h3>
              <span className="mono tt-sec">{formatNumber(rows.length)} módulos</span>
            </header>
            <table className="table" data-testid="by-module-table">
              <thead>
                <tr>
                  <th>Módulo</th>
                  <th className="r">Costo</th>
                  <th className="r">Sesiones</th>
                  <th className="r">Usuarios</th>
                  <th className="r">Costo/sesión</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="muted" style={{ textAlign: 'center' }}>
                      Sin datos en el rango / filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.module ?? NO_MODULE}>
                      <td className="mono tt-sec">{r.module ?? NO_MODULE}</td>
                      <td className="r mono">{nanoToUsdCompact(r.costNano)}</td>
                      <td className="r mono">{formatNumber(r.sessionCount)}</td>
                      <td className="r mono">{formatNumber(r.activeUsers)}</td>
                      <td className="r mono">{nanoToUsdCompact(r.costPerSessionNano)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </div>
      )}
    </>
  );
}
